import { initializeApp, getApps } from "firebase/app";
import {
    browserSessionPersistence,
    createUserWithEmailAndPassword,
    getAuth,
    sendEmailVerification,
    setPersistence,
    signInWithEmailAndPassword,
    signOut,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firebaseConfig } from "../firebaseConfig";

const EMAIL_WALLET_APP_NAME = "muulti-email-wallet-bootstrap";

function getEmailWalletApp() {
    return getApps().find((app) => app.name === EMAIL_WALLET_APP_NAME) ||
        initializeApp(firebaseConfig, EMAIL_WALLET_APP_NAME);
}

let persistenceReady = null;

export function getEmailWalletAuth() {
    const auth = getAuth(getEmailWalletApp());
    if (!persistenceReady) {
        persistenceReady = setPersistence(auth, browserSessionPersistence).catch((err) => {
            console.warn("Email wallet auth persistence setup failed:", err);
        });
    }
    return auth;
}

function getEmailWalletFunctions() {
    return getFunctions(getEmailWalletApp());
}

async function getEmailWalletIdToken() {
    const user = getEmailWalletAuth().currentUser;
    if (!user) {
        throw new Error("Email wallet login is required.");
    }
    return user.getIdToken(true);
}

async function withEmailWalletPersistence() {
    getEmailWalletAuth();
    if (persistenceReady) await persistenceReady;
}

export async function createEmailWalletUser(email, password) {
    await withEmailWalletPersistence();
    const auth = getEmailWalletAuth();
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    await sendEmailWalletVerification(credential.user);
    return credential.user;
}

export async function signInEmailWalletUser(email, password) {
    await withEmailWalletPersistence();
    const auth = getEmailWalletAuth();
    const credential = await signInWithEmailAndPassword(auth, email, password);
    return credential.user;
}

export async function signOutEmailWalletUser() {
    const auth = getEmailWalletAuth();
    await signOut(auth);
}

export function getCurrentEmailWalletUser() {
    return getEmailWalletAuth().currentUser;
}

export async function refreshEmailWalletUser() {
    const user = getCurrentEmailWalletUser();
    if (!user) {
        throw new Error("Email wallet login is required.");
    }
    await user.reload();
    await user.getIdToken(true);
    return getCurrentEmailWalletUser();
}

export async function ensureEmailWalletVerified({ sendIfNeeded = true } = {}) {
    const user = await refreshEmailWalletUser();
    if (user?.emailVerified) {
        return user;
    }
    if (sendIfNeeded) {
        await sendEmailWalletVerification(user);
    }
    return null;
}

export async function sendEmailWalletVerification(user = getCurrentEmailWalletUser()) {
    if (!user) {
        throw new Error("Email wallet login is required.");
    }
    if (user.emailVerified) return;
    await sendEmailVerification(user);
}

export async function saveEmailWalletRecord({ publicKey, encryptedWallet }) {
    const saveEmailWallet = httpsCallable(getEmailWalletFunctions(), "saveEmailWallet");
    const emailWalletIdToken = await getEmailWalletIdToken();
    const res = await saveEmailWallet({ publicKey, encryptedWallet, emailWalletIdToken });
    return res.data;
}

export async function getEmailWalletRecord() {
    const getEmailWallet = httpsCallable(getEmailWalletFunctions(), "getEmailWallet");
    const emailWalletIdToken = await getEmailWalletIdToken();
    const res = await getEmailWallet({ emailWalletIdToken });
    return res.data;
}
